import { Copy, Download, TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Checkbox } from '@nucleus-commerce/ui/components/checkbox';
import { DialogFooter } from '@nucleus-commerce/ui/components/dialog';
import { Label } from '@nucleus-commerce/ui/components/label';
import { toast } from '@nucleus-commerce/ui/components/sonner';

import { downloadText } from '../api/download-text';
import { recoveryCodesText } from '../api/mappers';

export interface RecoveryCodesPanelProps {
  codes: readonly string[];
  /** The account email, written into the downloaded file. */
  account: string;
  onDone: () => void;
}

/**
 * The ten recovery codes, shown once. Done stays disabled until the user confirms they saved
 * them, because Nucleus keeps only hashes and cannot show them again.
 */
export function RecoveryCodesPanel({ codes, account, onDone }: RecoveryCodesPanelProps) {
  const { t } = useTranslation('auth');
  const [saved, setSaved] = useState(false);
  const checkboxId = useId();
  const text = () =>
    recoveryCodesText(
      [
        t('recoveryCodes.fileTitle', { account }),
        t('recoveryCodes.fileGenerated', { date: new Date().toISOString() }),
        t('recoveryCodes.fileHint'),
      ],
      codes,
    );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      toast.success(t('recoveryCodes.copied'));
    } catch {
      toast.error(t('recoveryCodes.copyFailed'));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Alert>
        <TriangleAlert aria-hidden />
        <AlertTitle>{t('recoveryCodes.onceTitle')}</AlertTitle>
        <AlertDescription>{t('recoveryCodes.once')}</AlertDescription>
      </Alert>
      <ol
        aria-label={t('recoveryCodes.listLabel')}
        className="bg-muted grid grid-cols-2 gap-x-6 gap-y-1.5 rounded-md p-4 font-mono text-sm select-all"
        data-testid="recovery-codes"
      >
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" onClick={() => void copy()}>
          <Copy aria-hidden />
          {t('recoveryCodes.copy')}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => downloadText('nucleus-recovery-codes.txt', text())}
        >
          <Download aria-hidden />
          {t('recoveryCodes.download')}
        </Button>
      </div>
      <div className="flex items-center gap-2">
        <Checkbox
          id={checkboxId}
          checked={saved}
          onCheckedChange={(value) => setSaved(value === true)}
        />
        <Label htmlFor={checkboxId}>{t('recoveryCodes.confirm')}</Label>
      </div>
      <DialogFooter>
        <Button type="button" onClick={onDone} disabled={!saved}>
          {t('recoveryCodes.done')}
        </Button>
      </DialogFooter>
    </div>
  );
}
